import type { KyInstance } from 'ky';

import { ensureError, omit } from '@enterprise/core';
import type { OperationResultWithData, PagedData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { PagedMailingQuery } from './types/paged-mailing-query.type';
import type { PagedMailing } from './types/paged-mailing.type';

/**
 * Retrieves a paginated list of mailings from the API.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters for pagination and filtering mailings.
 * @param isExternal - Whether to use the external mailings endpoint. Defaults to `false`.
 * @returns A promise that resolves to an operation result containing paged mailing data.
 * @throws Throws an error if the request fails.
 */
export const getPagedMailings = async (instance: KyInstance, query: PagedMailingQuery, isExternal = false) => {
  try {
    const { isClosed = false, ...searchParams } = omit(query, 'filter', 'sort');

    return await instance
      .get<OperationResultWithData<PagedData<PagedMailing>>>(
        isExternal ? Uri.mailing.getPagedExternal : Uri.mailing.getPaged,
        {
          searchParams: {
            ...searchParams,
            isClosed,
          },
        }
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
